import { FC } from "react";

import { Box, Row, SystemStyleObject, Text, TraitIcon } from "@hightouch/htui";
import isEqual from "lodash/isEqual";
import { singular } from "pluralize";

import { createNewTraitSubcondition } from "src/components/explore/utils/condition-builders";
import { captureException } from "src/lib/monitoring";
import { STATIC_ARRAY } from "src/pages/analytics/state/constants";
import {
  ConditionType,
  getInitialTraitColumn,
  TraitCondition,
} from "src/types/visual";

import { GroupedComboboxFilter } from "./combobox-filter";
import { AndOrToggleButton, XButton } from "./condition-buttons";
import { PropertyOperatorValueFilter } from "./property-operator-value-filter";
import { TraitInlineSubconditionsProps } from "./trait-filter";
import { useFilterableOptionsContext } from "src/components/explore/filter-popover/filterable-options-hook";

type NestedTraitFilterProps = {
  modelId: string | null;
  condition: TraitCondition;
  isFirstCondition?: boolean;
  isWrappedWithAndCondition?: boolean;
  onRemove?(): void;
  onToggleWrappingConditionType(
    conditionType: ConditionType.And | ConditionType.Or,
  ): void;
  containerSx?: SystemStyleObject;
  hideAndOrToggleButton?: boolean;
};

export const NestedTraitFilter: FC<
  Readonly<
    NestedTraitFilterProps &
      TraitInlineSubconditionsProps & { semanticType: string }
  >
> = ({
  condition,
  containerSx,
  hideAndOrToggleButton = false,
  isFirstCondition = false,
  isWrappedWithAndCondition = true,
  semanticType,
  onToggleWrappingConditionType,
  ...props
}) => {
  const { parent } = props;

  const { traits, isLoading } = useFilterableOptionsContext();

  // TODO(XX): Add validation

  return (
    <Row sx={containerSx}>
      {isFirstCondition && (
        <Box mt={1.5} mr={2}>
          <Text color="text.secondary">
            Where {singular(semanticType)} members&apos;
          </Text>
        </Box>
      )}
      {!isFirstCondition && !hideAndOrToggleButton && (
        <AndOrToggleButton
          isDisabled={props.isDisabled}
          conditionType={
            isWrappedWithAndCondition ? ConditionType.And : ConditionType.Or
          }
          onClick={() =>
            onToggleWrappingConditionType(
              isWrappedWithAndCondition ? ConditionType.Or : ConditionType.And,
            )
          }
        />
      )}

      <Row gap={2} alignItems="flex-start">
        {/* TODO(nolan): replace with filter popover system */}
        <GroupedComboboxFilter
          isLoading={isLoading}
          isDisabled={props.isDisabled}
          optionGroups={[
            {
              label: "Traits",
              options: traits ?? STATIC_ARRAY,
            },
          ]}
          optionAccessories={() => ({
            leftAccessory: {
              type: "icon-box",
              icon: <TraitIcon />,
              color: "yellow",
            },
          })}
          optionDescription={(option) => option.description || ""}
          placeholder="Select a trait"
          width="2xs"
          valueLabel={(option) => option.name}
          optionLabel={(option) => option.name}
          optionValue={getInitialTraitColumn}
          value={condition.property ?? undefined}
          onChange={(value) => {
            const trait = traits?.find((trait) =>
              isEqual(getInitialTraitColumn(trait), value),
            );

            if (!trait) {
              captureException(new Error("Trait not found"));
              return;
            }

            props.onUpdate(() => createNewTraitSubcondition(trait));
          }}
        />

        <PropertyOperatorValueFilter
          isDisabled={props.isDisabled}
          condition={condition}
          loadingSuggestions={false}
          operatorError={null}
          valueError={null}
          suggestions={STATIC_ARRAY}
          allowPropertyComparison={false}
          parent={parent}
          onUpdate={props.onUpdate}
        />

        {props.onRemove && (
          <XButton isDisabled={props.isDisabled} onRemove={props.onRemove} />
        )}
      </Row>
    </Row>
  );
};
